import { useEffect, useState } from 'react';
import { API_BASE } from '../client/config';
import { useAdminRole } from './useAdminRole';

// =============================================================================
// BO operator gate (W5 IA).
//
// BRICK 어드민(BO) 메뉴·라우트는 bo_operator에게만 노출한다. AuthMe에는 BO
// 플래그가 없으므로 (서버가 /api/bo/* 를 allow-list로 가드함) FE는 BO 운영자
// 전용 엔드포인트 접근 가능 여부로 판정한다: `/api/bo/operators` 가 200이면
// 운영자, 401/403 이면 비운영자.
//
// 2026-09-16 검수 S2 — 예전에는 모든 계정이 화면마다 이 엔드포인트를 두드려
// 403이 감사 로그(`scope.denied`)를 500건 채웠다. BO 운영자는 반드시 h9_super
// 이므로, 등급이 아닐 때는 아예 부르지 않고 'denied'로 끝낸다. 호출은 등급이
// 확인된 계정만, 그것도 모듈 캐시로 한 번만 한다.
//
// 판정: 명시적인 성공 응답만 허용함. 404·네트워크 오류·401/403은 차단함.
// AUTH off 로컬도 실제 서버의 성공 응답으로 판정함.
// =============================================================================

export type BoGate = 'checking' | 'allowed' | 'denied';

let cached: BoGate | null = null;
let inflight: Promise<BoGate> | null = null;
const listeners = new Set<(gate: BoGate) => void>();

function probe(): Promise<BoGate> {
  if (cached) return Promise.resolve(cached);
  if (inflight) return inflight;
  inflight = fetch(`${API_BASE}/api/bo/operators`)
    .then((r) => (r.ok ? 'allowed' : 'denied') as BoGate)
    .catch(() => 'denied' as BoGate)
    .then((gate) => {
      cached = gate;
      inflight = null;
      listeners.forEach((fn) => fn(gate));
      return gate;
    });
  return inflight;
}

/** 테스트 전용 — 모듈 캐시 초기화. */
export function resetBoOperatorCache(): void {
  cached = null;
  inflight = null;
}

export function useBoOperator(): BoGate {
  const adminRole = useAdminRole();
  const [gate, setGate] = useState<BoGate>(() => cached ?? 'checking');

  useEffect(() => {
    if (adminRole === 'checking') return;
    // BO 운영자가 될 수 없는 등급 — 권한 없는 엔드포인트를 두드리지 않는다.
    if (adminRole !== 'h9_super') { setGate('denied'); return; }
    listeners.add(setGate);
    void probe().then(setGate);
    return () => { listeners.delete(setGate); };
  }, [adminRole]);

  return gate;
}
